// Research history list management

import { getAuthHeaders, getSubscriptionStatusUrl, authenticatedFetch } from '../api/auth.js';
import { showSnackbar } from './snackbar.js';
import { getCurrentResearchId, setCurrentResearchId, clearActiveResearchIdOnServer } from '../state/research.js';

let researchMenuListenerAttached = false;

function closeAllResearchMenus() {
    document.querySelectorAll('.research-session-actions-menu.open').forEach(menu => {
        menu.classList.remove('open');
        const parentItem = menu.closest('.research-session-item');
        if (parentItem) {
            parentItem.classList.remove('menu-open');
        }
    });
}

function attachResearchMenuCloseListener() {
    if (researchMenuListenerAttached) {
        return;
    }
    document.addEventListener('click', (event) => {
        if (!event.target.closest('.research-session-actions')) {
            closeAllResearchMenus();
        }
    });
    researchMenuListenerAttached = true;
}

export function showShareModal(researchId) {
    let overlay = document.getElementById('shareResearchModal');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'shareResearchModal';
        overlay.className = 'modal-overlay hidden';
        overlay.innerHTML = `
            <div class="modal-content share-modal-content">
                <div class="modal-header">
                    <h3>Share Research</h3>
                    <button class="modal-close-btn" id="shareModalCloseBtn" title="Close">
                        <span class="material-symbols-rounded notranslate">close</span>
                    </button>
                </div>
                <div class="modal-body share-modal-body">
                    <p class="share-modal-description">Anyone with this link can view this research.</p>
                    <div class="share-modal-url-row">
                        <input type="text" id="shareModalUrlInput" class="share-modal-url-input" readonly />
                        <button type="button" id="shareModalCopyBtn" class="apply-filter-btn share-modal-copy-btn">
                            <span>Copy Link</span>
                        </button>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(overlay);

        const closeShareModal = () => {
            overlay.classList.add('hidden');
            document.body.style.overflow = '';
            document.removeEventListener('keydown', shareModalEscapeHandler);
        };

        const shareModalEscapeHandler = (e) => {
            if (e.key === 'Escape' && !overlay.classList.contains('hidden')) {
                closeShareModal();
            }
        };

        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) closeShareModal();
        });

        const closeBtn = overlay.querySelector('#shareModalCloseBtn');
        if (closeBtn) {
            closeBtn.addEventListener('click', closeShareModal);
        }

        overlay._shareModalEscapeHandler = shareModalEscapeHandler;
    }

    const shareUrl = `${window.location.origin}/share/${researchId}`;
    const urlInput = overlay.querySelector('#shareModalUrlInput');
    if (urlInput) {
        urlInput.value = shareUrl;
    }

    const copyBtn = overlay.querySelector('#shareModalCopyBtn');
    if (copyBtn) {
        const originalHtml = copyBtn.innerHTML;
        copyBtn.onclick = () => {
            navigator.clipboard.writeText(shareUrl).then(() => {
                copyBtn.innerHTML = '<span>Copied!</span>';
                setTimeout(() => {
                    copyBtn.innerHTML = originalHtml;
                }, 2000);
            }).catch(() => {
                showSnackbar('Failed to copy link', 'error', 'warning', 3000);
            });
        };
    }

    document.addEventListener('keydown', overlay._shareModalEscapeHandler);
    overlay.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
}

function showDeleteConfirmModal(onConfirm, onCancel) {
    let overlay = document.getElementById('deleteConfirmModal');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'deleteConfirmModal';
        overlay.className = 'modal-overlay hidden';
        overlay.innerHTML = `
            <div class="modal-content delete-confirm-modal-content">
                <div class="modal-header">
                    <h3>Delete Research</h3>
                    <button class="modal-close-btn" id="deleteConfirmModalCloseBtn" title="Close">
                        <span class="material-symbols-rounded notranslate">close</span>
                    </button>
                </div>
                <div class="modal-body delete-confirm-modal-body">
                    <p class="delete-confirm-description">Are you sure you want to delete this research? This action cannot be undone.</p>
                    <div class="delete-confirm-actions">
                        <button type="button" id="deleteConfirmCancelBtn" class="reset-filter-btn">Cancel</button>
                        <button type="button" id="deleteConfirmDeleteBtn" class="apply-filter-btn delete-confirm-delete-btn">Delete</button>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(overlay);

        const closeModal = () => {
            overlay.classList.add('hidden');
            document.body.style.overflow = '';
            document.removeEventListener('keydown', escapeHandler);
        };

        const escapeHandler = (e) => {
            if (e.key === 'Escape' && !overlay.classList.contains('hidden')) {
                closeModal();
                if (overlay._onCancel) overlay._onCancel();
            }
        };

        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                closeModal();
                if (overlay._onCancel) overlay._onCancel();
            }
        });

        overlay.querySelector('#deleteConfirmModalCloseBtn').addEventListener('click', () => {
            closeModal();
            if (overlay._onCancel) overlay._onCancel();
        });

        overlay.querySelector('#deleteConfirmCancelBtn').addEventListener('click', () => {
            closeModal();
            if (overlay._onCancel) overlay._onCancel();
        });

        overlay.querySelector('#deleteConfirmDeleteBtn').addEventListener('click', () => {
            closeModal();
            if (overlay._onConfirm) overlay._onConfirm();
        });

        overlay._escapeHandler = escapeHandler;
    }

    overlay._onConfirm = onConfirm;
    overlay._onCancel = onCancel;
    document.addEventListener('keydown', overlay._escapeHandler);
    overlay.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
}

// Callbacks for functions that need to be called from pre/legacy.js
let loadResearchHistoryCallback = null;
let loadResearchSessionCallback = null;

// Pagination state for research history infinite scroll
const BATCH_SIZE = 10;
let researchHistoryOffset = 0;
let researchHistoryHasMore = false;
let researchHistoryIsPro = false;
let researchHistoryLoadingMore = false;
let researchHistoryIntersectionObserver = null;

export function setResearchHistoryCallbacks(callbacks) {
    if (callbacks.loadResearchHistory) loadResearchHistoryCallback = callbacks.loadResearchHistory;
    if (callbacks.loadResearchSession) loadResearchSessionCallback = callbacks.loadResearchSession;
}

async function updateEmptyStateMessage(emptyDiv) {
    const emptyText = document.getElementById('researchHistoryEmptyText');
    const upgradeBtn = document.getElementById('researchHistoryUpgradeBtn');

    if (!emptyText) return;

    const isLoggedIn = localStorage.getItem('access_token') !== null;

    if (!isLoggedIn) {
        emptyText.textContent = 'No research history yet.';
        if (upgradeBtn) upgradeBtn.style.display = 'none';
        return;
    }

    if (!researchHistoryIsPro) {
        emptyText.textContent = 'No research history yet. Upgrade to Pro to automatically save all your research topics and searches.';
        if (upgradeBtn) {
            upgradeBtn.style.display = 'inline-block';
            upgradeBtn.textContent = 'Upgrade to Pro';
        }
        return;
    }

    emptyText.textContent = 'No research history yet. Start a search to create your first research topic!';
    if (upgradeBtn) upgradeBtn.style.display = 'none';
}

function setupLoadMoreObserver() {
    const loadMoreDiv = document.getElementById('researchHistoryLoadMore');
    const scrollRoot = document.getElementById('researchHistorySidebar');
    if (!loadMoreDiv || !scrollRoot || researchHistoryIntersectionObserver) return;

    researchHistoryIntersectionObserver = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;
                if (!researchHistoryHasMore || researchHistoryLoadingMore) continue;
                loadMoreResearchHistory();
            }
        },
        { root: scrollRoot, rootMargin: '100px', threshold: 0 }
    );
    researchHistoryIntersectionObserver.observe(loadMoreDiv);
}

function updateLoadMoreVisibility() {
    const loadMoreDiv = document.getElementById('researchHistoryLoadMore');
    if (!loadMoreDiv) return;
    if (researchHistoryHasMore) {
        loadMoreDiv.classList.remove('hidden');
        loadMoreDiv.classList.toggle('loading', researchHistoryLoadingMore);
    } else {
        loadMoreDiv.classList.add('hidden');
    }
}

async function loadMoreResearchHistory() {
    if (researchHistoryLoadingMore || !researchHistoryHasMore) return;
    researchHistoryLoadingMore = true;
    updateLoadMoreVisibility();

    const sessionsList = document.getElementById('researchSessionsList');
    try {
        const response = await authenticatedFetch(
            `/research-sessions?limit=${BATCH_SIZE}&offset=${researchHistoryOffset}`,
            { headers: getAuthHeaders() }
        );
        if (!response.ok) throw new Error('Failed to load more');
        const data = await response.json();
        const sessions = data.research_sessions || data.sessions || [];
        researchHistoryHasMore = data.has_more === true;
        researchHistoryOffset += sessions.length;

        if (sessionsList) {
            sessions.forEach(session => {
                const sessionItem = createResearchSessionItem(session, researchHistoryIsPro);
                sessionsList.appendChild(sessionItem);
            });
        }
    } catch (error) {
        console.error('[Research] Error loading more research history:', error);
        researchHistoryHasMore = false;
    } finally {
        researchHistoryLoadingMore = false;
        updateLoadMoreVisibility();
    }
}



export async function loadResearchHistory() {
    const loadingDiv = document.getElementById('researchHistoryLoading');
    const emptyDiv = document.getElementById('researchHistoryEmpty');
    const sessionsList = document.getElementById('researchSessionsList');
    const loadMoreDiv = document.getElementById('researchHistoryLoadMore');

    // Reset pagination state for full reload
    researchHistoryOffset = 0;
    researchHistoryHasMore = false;
    researchHistoryLoadingMore = false;
    if (loadMoreDiv) loadMoreDiv.classList.add('hidden');

    // Show loading state
    if (loadingDiv) {
        loadingDiv.classList.remove('hidden');
        loadingDiv.style.display = 'flex';
    }
    if (emptyDiv) emptyDiv.classList.add('hidden');
    if (sessionsList) sessionsList.innerHTML = '';

    try {
        const response = await authenticatedFetch(
            `/research-sessions?limit=${BATCH_SIZE}&offset=0`,
            { headers: getAuthHeaders() }
        );

        if (response.ok) {
            const data = await response.json();
            const sessions = data.research_sessions || data.sessions || [];
            researchHistoryHasMore = data.has_more === true;
            researchHistoryOffset = sessions.length;
            researchHistoryIsPro = data.is_pro === true;

            // Hide loading
            if (loadingDiv) {
                loadingDiv.classList.add('hidden');
                loadingDiv.style.display = 'none';
            }

            if (sessions.length === 0) {
                if (emptyDiv) {
                    emptyDiv.classList.remove('hidden');
                    await updateEmptyStateMessage(emptyDiv);
                }
            } else {
                if (sessionsList) {
                    sessions.forEach(session => {
                        const sessionItem = createResearchSessionItem(session, researchHistoryIsPro);
                        sessionsList.appendChild(sessionItem);
                    });
                }

                updateLoadMoreVisibility();
                setupLoadMoreObserver();
            }
        } else {
            console.error('[Research] Failed to load research history');
            if (loadingDiv) {
                loadingDiv.classList.add('hidden');
                loadingDiv.style.display = 'none';
            }
            if (emptyDiv) {
                emptyDiv.classList.remove('hidden');
                await updateEmptyStateMessage(emptyDiv);
            }
        }
    } catch (error) {
        console.error('[Research] Error loading research history:', error);
        if (loadingDiv) loadingDiv.classList.add('hidden');
        if (emptyDiv) {
            emptyDiv.classList.remove('hidden');
            await updateEmptyStateMessage(emptyDiv);
        }
    } finally {
        if (loadingDiv) {
            loadingDiv.classList.add('hidden');
            loadingDiv.style.display = 'none';
        }
    }
}

export function createResearchSessionItem(session, isPro = true) {
    const item = document.createElement('div');
    item.className = 'research-session-item';
    item.dataset.researchId = session.research_id;
    if (session.pinned) {
        item.classList.add('pinned');
    }
    
    const searchCount = document.createElement('div');
    searchCount.className = 'research-session-search-count';
    const searchCountText = session.search_count || 0;
    searchCount.innerHTML = `<span class="material-symbols-rounded notranslate" style="font-size: 1rem;">search_insights</span> ${searchCountText} search${searchCountText !== 1 ? 'es' : ''}`;
    
    const date = document.createElement('div');
    date.className = 'research-session-date';
    if (session.created_at) {
        const dateObj = new Date(session.created_at * 1000);
        date.textContent = dateObj.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    }
    
    const header = document.createElement('div');
    header.className = 'research-session-header';

    const actions = document.createElement('div');
    actions.className = 'research-session-actions';

    const actionsButton = document.createElement('button');
    actionsButton.type = 'button';
    actionsButton.className = 'research-session-actions-btn';
    actionsButton.innerHTML = '<span class="material-symbols-rounded notranslate">more_vert</span>';

    const actionsMenu = document.createElement('div');
    actionsMenu.className = 'research-session-actions-menu';

    const isPinned = Boolean(session.pinned);
    const pinItem = document.createElement('button');
    pinItem.type = 'button';
    pinItem.className = 'research-session-menu-item';
    const pinMenuIcon = document.createElement('span');
    pinMenuIcon.className = 'material-symbols-rounded notranslate';
    const fillSetting = isPinned ? 0 : 1;
    pinMenuIcon.textContent = isPinned ? 'keep_off' : 'push_pin';
    pinMenuIcon.style.setProperty('font-variation-settings', `'FILL' ${fillSetting}, 'wght' 600, 'GRAD' 0, 'opsz' 20`, 'important');
    pinItem.appendChild(pinMenuIcon);
    const pinMenuLabel = document.createElement('span');
    pinMenuLabel.textContent = isPinned ? 'Unpin' : 'Pin';
    pinItem.appendChild(pinMenuLabel);

    const renameItem = document.createElement('button');
    renameItem.type = 'button';
    renameItem.className = 'research-session-menu-item';
    renameItem.innerHTML = '<span class="material-symbols-rounded notranslate">edit</span>Rename';

    const shareItem = document.createElement('button');
    shareItem.type = 'button';
    shareItem.className = 'research-session-menu-item';
    shareItem.innerHTML = '<span class="material-symbols-rounded notranslate">share</span>Share';

    const deleteItem = document.createElement('button');
    deleteItem.type = 'button';
    deleteItem.className = 'research-session-menu-item';
    deleteItem.innerHTML = '<span class="material-symbols-rounded notranslate">delete</span>Delete';

    actionsMenu.appendChild(pinItem);
    actionsMenu.appendChild(renameItem);
    if (isPro) actionsMenu.appendChild(shareItem);
    actionsMenu.appendChild(deleteItem);

    actions.appendChild(actionsButton);
    actions.appendChild(actionsMenu);
    header.appendChild(actions);

    const pinIndicator = document.createElement('div');
    pinIndicator.className = 'research-session-pin-indicator';
    const pinIcon = document.createElement('span');
    pinIcon.className = 'material-symbols-rounded notranslate';
    pinIcon.textContent = 'push_pin';
    pinIcon.style.setProperty('font-variation-settings', `'FILL' 1, 'wght' 600, 'GRAD' 0, 'opsz' 20`, 'important');
    pinIndicator.appendChild(pinIcon);

    const leftSection = document.createElement('div');
    leftSection.className = 'research-session-left';

    const titleContainer = document.createElement('div');
    titleContainer.className = 'research-session-title-container';
    const title = document.createElement('div');
    title.className = 'research-session-title';
    title.textContent = session.title || 'Untitled Research';
    titleContainer.appendChild(title);
    leftSection.appendChild(titleContainer);

    header.insertBefore(leftSection, actions);
    header.insertBefore(pinIndicator, leftSection);

    const meta = document.createElement('div');
    meta.className = 'research-session-meta';
    meta.appendChild(searchCount);
    meta.appendChild(date);

    item.appendChild(header);
    item.appendChild(meta);

    attachResearchMenuCloseListener();

    let renameMode = false;

    const exitRenameMode = () => {
        if (!renameMode) return;
        renameMode = false;
        item.classList.remove('renaming');
        actionsButton.style.display = '';
        titleContainer.innerHTML = '';
        title.textContent = session.title || 'Untitled Research';
        titleContainer.appendChild(title);
    };

    const enterRenameMode = () => {
        if (renameMode) return;
        renameMode = true;
        item.classList.add('renaming');
        actionsButton.style.display = 'none';

        const currentTitle = session.title || 'Untitled Research';

        const renameWrapper = document.createElement('div');
        renameWrapper.className = 'research-session-rename-wrapper';
        renameWrapper.addEventListener('click', (event) => {
            event.stopPropagation();
        });

        const renameInput = document.createElement('textarea');
        renameInput.value = currentTitle;
        renameInput.placeholder = 'Rename research';
        renameInput.className = 'research-session-rename-input';
        renameInput.rows = 1;
        renameInput.addEventListener('click', (event) => event.stopPropagation());

        const adjustTextareaHeight = () => {
            renameInput.style.height = 'auto';
            renameInput.style.height = `${renameInput.scrollHeight}px`;
        };
        adjustTextareaHeight();

        renameInput.addEventListener('input', adjustTextareaHeight);

        const controls = document.createElement('div');
        controls.className = 'research-session-rename-controls';
        controls.addEventListener('click', (event) => event.stopPropagation());

        const saveBtn = document.createElement('button');
        saveBtn.type = 'button';
        saveBtn.className = 'icon-btn';
        saveBtn.innerHTML = '<span class="material-symbols-rounded notranslate">check</span>';

        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.className = 'icon-btn';
        cancelBtn.innerHTML = '<span class="material-symbols-rounded notranslate">close</span>';

        controls.appendChild(saveBtn);
        controls.appendChild(cancelBtn);
        renameWrapper.appendChild(renameInput);
        renameWrapper.appendChild(controls);

        titleContainer.innerHTML = '';
        titleContainer.appendChild(renameWrapper);
        renameInput.focus();
        renameInput.select();

        const handleCancel = () => {
            exitRenameMode();
        };

        const handleSave = async () => {
            const newValue = renameInput.value.trim();
            if (!newValue) {
                alert('Title cannot be empty.');
                return;
            }
            if (newValue === currentTitle) {
                exitRenameMode();
                return;
            }
            item.classList.add('pending-action');
            try {
                await renameResearchSession(session.research_id, newValue);
                session.title = newValue;
            } catch (error) {
                console.error('[Research] Failed to rename session:', error);
                alert('Failed to rename research session. Please try again.');
                item.classList.remove('pending-action');
                exitRenameMode();
            }
        };

        saveBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            handleSave();
        });
        cancelBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            handleCancel();
        });
        renameInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSave();
            } else if (event.key === 'Escape') {
                event.preventDefault();
                handleCancel();
            }
        });
    };

    actionsButton.addEventListener('click', (event) => {
        event.stopPropagation();
        const isOpen = actionsMenu.classList.contains('open');
        closeAllResearchMenus();
        if (!isOpen) {
            actionsMenu.classList.add('open');
            item.classList.add('menu-open');
        }
    });

    pinItem.addEventListener('click', async (event) => {
        event.stopPropagation();
        closeAllResearchMenus();
        item.classList.remove('menu-open');
        item.classList.add('pending-action');
        try {
            await pinResearchSession(session.research_id, !isPinned);
        } catch (error) {
            console.error('[Research] Failed to pin session:', error);
            alert('Failed to update pin status. Please try again.');
            item.classList.remove('pending-action');
        }
    });

    renameItem.addEventListener('click', (event) => {
        event.stopPropagation();
        closeAllResearchMenus();
        item.classList.remove('menu-open');
        enterRenameMode();
    });

    shareItem.addEventListener('click', (event) => {
        event.stopPropagation();
        closeAllResearchMenus();
        item.classList.remove('menu-open');
        showShareModal(session.research_id);
    });

    deleteItem.addEventListener('click', (event) => {
        event.stopPropagation();
        closeAllResearchMenus();
        item.classList.remove('menu-open');
        showDeleteConfirmModal(
            async () => {
                item.classList.add('pending-action');
                try {
                    await deleteResearchSession(session.research_id);
                    showSnackbar('Research deleted', 'info', 'delete', 3000);
                } catch (error) {
                    console.error('[Research] Failed to delete session:', error);
                    showSnackbar('Failed to delete research session. Please try again.', 'error', 'warning', 5000);
                    item.classList.remove('pending-action');
                }
            },
            () => {}
        );
    });

    // Click handler to load research session
    item.addEventListener('click', () => {
        if (renameMode) {
            return;
        }
        // Call loadResearchSession via callback
        if (loadResearchSessionCallback) {
            loadResearchSessionCallback(session.research_id);
        } else if (window.bms && window.bms.loadResearchSession) {
            window.bms.loadResearchSession(session.research_id);
        }
    });

    return item;
}

async function pinResearchSession(researchId, shouldPin) {
    const headers = {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
    };
    const response = await authenticatedFetch(`/api/research-sessions/${researchId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ pinned: shouldPin })
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update pin state');
    }
    // Reload research history via callback
    if (loadResearchHistoryCallback) {
        await loadResearchHistoryCallback();
    } else if (window.bms && window.bms.loadResearchHistory) {
        await window.bms.loadResearchHistory();
    }
    console.log('[Research] Pinned to new position; refreshing list');
}

async function renameResearchSession(researchId, newTitle) {
    const headers = {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
    };
    const response = await authenticatedFetch(`/api/research-sessions/${researchId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ title: newTitle })
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to rename research session');
    }
    // Reload research history via callback
    if (loadResearchHistoryCallback) {
        await loadResearchHistoryCallback();
    } else if (window.bms && window.bms.loadResearchHistory) {
        await window.bms.loadResearchHistory();
    }
    console.log('[Research] Renamed research session; refreshing list');
}

export async function deleteResearchSession(researchId) {
    const headers = {
        ...getAuthHeaders()
    };
    const response = await authenticatedFetch(`/research-sessions/${researchId}`, {
        method: 'DELETE',
        headers
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to delete research session');
    }
    
    // Check if the deleted research is the currently loaded research
    const currentResearchId = getCurrentResearchId();
    if (currentResearchId === researchId) {
        console.log('[Research] Deleted research is currently loaded, refreshing to home page');
        // Show snackbar first, then navigate after delay so user sees the message
        showSnackbar('Research deleted', 'info', 'delete', 3000);
        await clearActiveResearchIdOnServer();
        setCurrentResearchId(null);
        sessionStorage.removeItem('bms_session_id');
        sessionStorage.removeItem('bms_has_searched');
        // Delay navigation so snackbar is visible
        setTimeout(() => {
            window.location.href = '/';
        }, 2000);
        return;
    }
    
    // Reload research history via callback
    if (loadResearchHistoryCallback) {
        await loadResearchHistoryCallback();
    } else if (window.bms && window.bms.loadResearchHistory) {
        await window.bms.loadResearchHistory();
    }
    console.log('[Research] Deleted research session; refreshing list');
}
